<template>
	<div
		:style="{zIndex}"
		class="fixed inset-0 h-full overflow-y-scroll !m-0 bg-black/20 flex justify-center backdrop-blur-[0.1rem] sm:backdrop-blur-xs"
		:class="alignTop
			? '!items-start pt-10 lg:pt-16 pb-10'
			: ['lg:items-center', {'items-center' : mobileCenter && !modalIsTaller, '!items-start': modalIsTaller, 'items-end': !modalIsTaller}]"
		tabindex="0"
	>
		<div
			ref="modal"
			class="
				relative
				z-50
				rem:max-w-[990px]
				w-full
				lg:w-[calc(100%-32px)]
				rounded-t-2.5xl
				lg:rounded-2.5xl
				shadow-sm
				border
				border-border-tertiary
				bg-background-primary
				lg:m-2
			"
			:class="[mobileCenter && 'rounded-2.5xl', alignTop && 'rounded-2.5xl']"
		>
			<div class="sticky top-0" :style="{zIndex: zIndex + 1}">
				<button
					class="p-2 absolute top-2 right-2 text-icon-primary bg-background-primary hover:bg-button-secondary-surface-hover rounded-full transition ease btn max-sm:shadow-gray-md"
					@click="emit('update:isOpen', false)"
				>
					<NuxtIcon name="x" filled class="text-2xl md:text-3xl" />
				</button>
			</div>
			<div ref="modalContainer" :class="!hideContainer && 'container'">
				<slot />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import {onClickOutside} from '@vueuse/core';
	import {storeToRefs} from 'pinia';
	import {useFocusTrap} from '@vueuse/integrations/useFocusTrap';
	import {useSelectboxStore} from '~/stores/selectbox';

	type Props = {
		isOpen?: boolean;
		mobileCenter?: boolean;
		hideContainer?: boolean;
		alignTop?: boolean;
	};

	const props = withDefaults(defineProps<Props>(), {isOpen: () => false});
	const emit = defineEmits(['update:isOpen']);

	const {isFrontModal, zIndex, register, unregister} = useModalStackEntry();

	const keydownListener = ref();
	const modalContainer = ref<HTMLDivElement | null>(null);

	const modal = ref<HTMLElement | null>(null);
	const {selectboxDropdown} = storeToRefs(useSelectboxStore());

	const {height: windowHeight} = useWindowSize();
	const {height: modalHeight} = useElementSize(modalContainer);
	const modalIsTaller = computed(() => modalHeight.value >= windowHeight.value);

	const {activate: activateFocusTrap, deactivate: deactivateFocusTrap} = useFocusTrap(modal);

	keydownListener.value = function (e: KeyboardEvent) {
		if (e.key === 'Escape' && isFrontModal.value) {
			emit('update:isOpen', false);
		}
	};

	onClickOutside(modal, () => {
		if (isFrontModal.value) {
			emit('update:isOpen', false);
		}
	}, {ignore: [selectboxDropdown]});

	onMounted(() => {
		document.addEventListener('keydown', keydownListener.value);
	});

	onBeforeUnmount(() => {
		document.removeEventListener('keydown', keydownListener.value);
		deactivateFocusTrap();

		if (props.isOpen) {
			unregister();
		}
	});

	watch([() => props.isOpen, isFrontModal], async ([open, front]) => {
		if (open && front) {
			await nextTick();
			activateFocusTrap();
		} else {
			deactivateFocusTrap();
		}
	}, {immediate: true});

	watch(() => props.isOpen, (open) => {
		if (open) {
			register();
		} else {
			unregister();
		}
	}, {immediate: true});
</script>
